import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Badge } from '../../styles/v2.styled';
import { checkSession } from '../../../Common/Utils/SsoSession';

const PORTFOLIO_TYPES = [
  {
    id: 'Developer',
    label: '프로그래밍',
    getUrl: '/request/GetDevPhoto_ORG',
    uploadUrl: '/upload/DeveloperFileUpload',
    deleteUrl: '/request/DeletePortpolio_developer',
    folder: 'DeveloperFile',
  },
  {
    id: 'Modeler',
    label: '모델링',
    getUrl: '/request/GetMdlPhoto_ORG',
    uploadUrl: '/upload/ModelerFileUpload',
    deleteUrl: '/request/DeletePortpolio_Modeler',
    folder: 'ModelerFile',
  },
  {
    id: 'Designer',
    label: '디자인',
    getUrl: '/request/GetDgnPhoto_ORG',
    uploadUrl: '/upload/DesignerFileUpload',
    deleteUrl: '/request/DeletePortpolio_Design',
    folder: 'DesignerFile',
  },
  {
    id: 'Infra',
    label: '인프라',
    getUrl: '/request/GetIfaPhoto_ORG',
    uploadUrl: '/upload/InfraFileUpload',
    deleteUrl: '/request/DeletePortpolio_Infra',
    folder: 'InfraFile',
  },
  {
    id: 'Media',
    label: '미디어',
    getUrl: '/request/GetMediaPhoto_ORG',
    uploadUrl: '/upload/MediaFileUpload',
    deleteUrl: '/request/DeletePortpolio_Media',
    folder: 'MediaFile',
  },
];

const Shell = styled.div`
  display: grid;
  gap: 18px;
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TabButton = styled.button`
  min-height: 38px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[400] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  padding: 0 14px;
  font-weight: 900;
  cursor: pointer;
`;

const Dashboard = styled.div`
  display: grid;
  grid-template-columns: 410px minmax(0, 1fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  background: ${({ theme }) => theme.colors.neutral[50]};

  h3 {
    margin: 8px 0 0;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1.08rem;
  }

  p {
    margin: 8px 0 0;
    color: ${({ theme }) => theme.colors.neutral[500]};
    line-height: 1.55;
    font-size: 0.9rem;
  }
`;

const PanelBody = styled.div`
  padding: 18px;
`;

const UploadZone = styled.label`
  display: grid;
  place-items: center;
  min-height: 210px;
  border: 1px dashed ${({ $dragOver, theme }) => ($dragOver ? theme.colors.primary[500] : theme.colors.neutral[300])};
  border-radius: 8px;
  background: ${({ $dragOver, theme }) => ($dragOver ? theme.colors.primary[50] : theme.colors.neutral[50])};
  color: ${({ theme }) => theme.colors.neutral[600]};
  cursor: pointer;
  text-align: center;
  padding: 24px;

  strong {
    display: block;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1.04rem;
    margin-bottom: 8px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 0.86rem;
  font-weight: 900;
`;

const Input = styled.input`
  min-height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const Textarea = styled.textarea`
  min-height: 120px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;
  resize: vertical;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.6;
`;

const Button = styled.button`
  min-height: 40px;
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[0])};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  padding: 0 14px;
  font-weight: 900;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Preview = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  button {
    position: absolute;
    top: 6px;
    right: 6px;
  }
`;

const GalleryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Thumb = styled.div`
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #ecfeff 0%, #f8fafc 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const CardBody = styled.div`
  padding: 14px;

  h4 {
    margin: 0 0 8px;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.neutral[600]};
    line-height: 1.55;
    font-size: 0.9rem;
  }
`;

const CardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 0 14px 14px;
`;

const Empty = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.neutral[300]};
  border-radius: 8px;
  padding: 38px 18px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  text-align: center;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

function getUploadBase() {
  return process.env.REACT_APP_UPLOAD_ENDPOINT || process.env.REACT_APP_API_ENDPOINT || '';
}

function getMediaBase() {
  return process.env.REACT_APP_API_ENDPOINT || '';
}

function getItemValue(item, ...keys) {
  for (const key of keys) {
    if (item && item[key] !== undefined && item[key] !== null) return item[key];
  }
  return '';
}

function buildImageSrc(item, typeConfig) {
  const filename = getItemValue(item, 'pix_loadpath', 'pix_loadPath', 'pix_filename', 'pix_filename');
  if (!filename) return '';
  const base = getMediaBase();
  return `${base}/antsnest/${typeConfig.folder}/${filename}`;
}

function WorkspacePortfolioManager() {
  const fileInputRef = useRef(null);
  const [session, setSession] = useState({});
  const [activeTypeId, setActiveTypeId] = useState('Developer');
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    title: '',
    contents: '',
    startDate: '',
    endDate: '',
    topic: '',
    task: '',
    category: '',
  });
  const [message, setMessage] = useState('');

  const activeType = useMemo(
    () => PORTFOLIO_TYPES.find((item) => item.id === activeTypeId) || PORTFOLIO_TYPES[0],
    [activeTypeId],
  );
  const isLoggedIn = session?.State === 'LOGIN';

  const loadItems = useCallback(async () => {
    try {
      const response = await fetch(activeType.getUrl, { credentials: 'include' });
      const text = await response.text();
      if (!response.ok || !text) {
        setItems([]);
        return;
      }
      const data = JSON.parse(text);
      setItems(Array.isArray(data) ? data : []);
      setMessage('');
    } catch (error) {
      setItems([]);
      setMessage('포트폴리오 데이터를 불러오지 못했습니다.');
    }
  }, [activeType]);

  useEffect(() => {
    checkSession().then((data) => setSession(data || {}));
  }, []);

  useEffect(() => {
    loadItems();
    setFiles([]);
    setPreviews([]);
  }, [loadItems, activeTypeId]);

  useEffect(() => {
    return () => previews.forEach((preview) => URL.revokeObjectURL(preview.url));
  }, [previews]);

  const appendFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (!nextFiles.length) return;
    const nextPreviews = nextFiles.map((file) => ({ name: file.name, url: URL.createObjectURL(file) }));
    setFiles((prev) => [...prev, ...nextFiles]);
    setPreviews((prev) => [...prev, ...nextPreviews]);
  };

  const removePreview = (index) => {
    URL.revokeObjectURL(previews[index]?.url);
    setFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setPreviews((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const upload = async () => {
    if (!files.length || !isLoggedIn) return;
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
      formData.append('img', file);
    });
    formData.append('userId', session.UserId || '');
    formData.append('contents', form.contents || form.title || '');
    formData.append('title', form.title);
    formData.append('startDate', form.startDate);
    formData.append('endDate', form.endDate);
    formData.append('topic', form.topic);
    formData.append('task', form.task);
    formData.append('category', form.category || activeType.label);

    try {
      const response = await fetch(`${getUploadBase()}${activeType.uploadUrl}`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) throw new Error('upload failed');
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
      setFiles([]);
      setPreviews([]);
      setForm({ title: '', contents: '', startDate: '', endDate: '', topic: '', task: '', category: '' });
      setMessage('업로드가 완료되었습니다.');
      await loadItems();
    } catch (error) {
      setMessage('업로드하지 못했습니다. 로그인과 업로드 서버를 확인하세요.');
    }
  };

  const removeItem = async (item) => {
    const key = getItemValue(item, 'pix_num', 'PIX_NUM');
    if (!key) return;
    try {
      await fetch(activeType.deleteUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Key: key }),
      });
      setItems((prev) => prev.filter((entry) => getItemValue(entry, 'pix_num', 'PIX_NUM') !== key));
    } catch (error) {
      setMessage('삭제하지 못했습니다.');
    }
  };

  return (
    <Shell>
      <TabList role="tablist" aria-label="포트폴리오 분류">
        {PORTFOLIO_TYPES.map((item) => (
          <TabButton
            key={item.id}
            type="button"
            $active={activeTypeId === item.id}
            onClick={() => setActiveTypeId(item.id)}
          >
            {item.label}
          </TabButton>
        ))}
        <Badge $variant={isLoggedIn ? 'success' : 'warning'}>{isLoggedIn ? '업로드 가능' : '로그인 필요'}</Badge>
        {message && <Badge $variant="default">{message}</Badge>}
      </TabList>

      <Dashboard>
        <Panel>
          <PanelHead>
            <Badge $variant="primary">{activeType.label}</Badge>
            <h3>새 포트폴리오 등록</h3>
            <p>이미지는 여러 장을 등록할 수 있고, 첫 번째 이미지가 대표 썸네일로 표시됩니다.</p>
          </PanelHead>
          <PanelBody>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => appendFiles(event.target.files)}
            />
            <UploadZone
              htmlFor=""
              $dragOver={dragOver}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                appendFiles(event.dataTransfer.files);
              }}
            >
              <div>
                <strong>이미지를 선택하거나 끌어오세요</strong>
                <span>PNG, JPG, JPEG, GIF 형식을 권장합니다.</span>
              </div>
            </UploadZone>
            {previews.length > 0 && (
              <PreviewGrid>
                {previews.map((preview, index) => (
                  <Preview key={`${preview.name}-${preview.url}`}>
                    <img src={preview.url} alt={preview.name} />
                    <Button type="button" onClick={() => removePreview(index)}>삭제</Button>
                  </Preview>
                ))}
              </PreviewGrid>
            )}
            <Field>제목<Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></Field>
            <Field>설명<Textarea value={form.contents} onChange={(event) => setForm({ ...form, contents: event.target.value })} /></Field>
            <Field>프로젝트 기간<Input type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} /></Field>
            <Field>종료일<Input type="date" value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} /></Field>
            <Field>주제<Input value={form.topic} onChange={(event) => setForm({ ...form, topic: event.target.value })} /></Field>
            <Field>담당 업무<Input value={form.task} onChange={(event) => setForm({ ...form, task: event.target.value })} /></Field>
            <Field>카테고리<Input value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} /></Field>
            <Button type="button" $primary onClick={upload} disabled={!isLoggedIn || !files.length} style={{ marginTop: 14 }}>
              포트폴리오 업로드
            </Button>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead>
            <GalleryHead>
              <div>
                <Badge $variant="default">{items.length}개</Badge>
                <h3>등록된 포트폴리오</h3>
              </div>
              <Button type="button" onClick={loadItems}>새로고침</Button>
            </GalleryHead>
          </PanelHead>
          <PanelBody>
            {items.length === 0 ? (
              <Empty>등록된 포트폴리오가 없습니다. 왼쪽에서 이미지를 업로드하세요.</Empty>
            ) : (
              <Gallery>
                {items.map((item) => {
                  const key = getItemValue(item, 'pix_num', 'PIX_NUM');
                  const src = buildImageSrc(item, activeType);
                  const contents = getItemValue(item, 'pix_contents', 'PIX_CONTENTS') || '설명 없음';
                  const filename = getItemValue(item, 'pix_filename', 'PIX_FILENAME') || `portfolio-${key}`;
                  return (
                    <Card key={key || filename}>
                      <Thumb>
                        {src ? <img src={src} alt={contents || filename} loading="lazy" /> : null}
                      </Thumb>
                      <CardBody>
                        <h4>{contents.slice(0, 28)}</h4>
                        <p>{filename}</p>
                      </CardBody>
                      <CardActions>
                        <Badge $variant="default">#{key}</Badge>
                        <Button type="button" onClick={() => removeItem(item)} disabled={!isLoggedIn}>삭제</Button>
                      </CardActions>
                    </Card>
                  );
                })}
              </Gallery>
            )}
          </PanelBody>
        </Panel>
      </Dashboard>
    </Shell>
  );
}

export default WorkspacePortfolioManager;
